/**
 * Zapis cache'u React Query do `localStorage`, żeby F5 malował dashboard
 * z dysku zamiast czekać na backend.
 *
 * Po co to w ogóle jest. `staleTime` dashboardu jest policzony do najbliższej
 * granicy odświeżenia MV (`lib/mv-stale-time.ts`) — czyli dane bywają świeże
 * godzinami. Cache React Query żyje jednak wyłącznie w pamięci karty, więc
 * każde F5 wysyłało komplet ~15 requestów mimo że żaden nie mógł przynieść
 * nowej liczby. Pomiar prod 2026-08-29: cały ekran 07:53:36–07:54:02 = 26 s,
 * na ciepłym cache bazy. Middleware ETag (`backend/shared/src/middleware/etag.rs`)
 * tego NIE ratuje — 304 oszczędza bajty, ale handler i tak wykonuje pełne
 * zapytanie, żeby policzyć hash.
 *
 * Bez nowej zależności: `@tanstack/query-persist-client-*` robi dokładnie to,
 * co poniżej, a `dehydrate`/`hydrate` są już w `@tanstack/react-query`.
 * Hydratacja jest SYNCHRONICZNA i leci przed pierwszym renderem, więc dane są
 * na miejscu, zanim komponent zdąży o nie zapytać — `PersistQueryClientProvider`
 * musiałby w tym celu wstrzymać render całej aplikacji.
 *
 * IZOLACJA. W tym produkcie apteki są dla siebie konkurencją, więc cache pod
 * żadnym pozorem nie może przeżyć zmiany tożsamości. Trzy zabezpieczenia:
 *  1. [`dropPersistedQueryCache`] wywoływane przy KAŻDYM przejściu stanu auth
 *     (login, logout, start i stop impersonacji, przepięcie tenanta),
 *  2. [`assertPersistedCacheOwner`] kasuje cache, gdy `/auth/me` po F5 zwróci
 *     inną osobę (organizacja + id) niż ta, która cache zapisała,
 *  3. `BUSTER` — deploy z inną wersją schematu odrzuca całość.
 *
 * Zapisujemy TYLKO rodziny kluczy z [`PERSISTED_KEY_PREFIXES`]. Cache admina,
 * reguł widoczności czy KSeF nie ma tu czego szukać: rośnie, zmienia się po
 * mutacjach i nie jest wąskim gardłem.
 *
 * Pojedyncze zapytanie może się wypisać z zapisu przez `meta: { persist: false }`
 * — używa tego lista SKU dashboardu, która przy 2 MB limitu zjadłaby budżet
 * całej reszty.
 */

import { dehydrate, hydrate, type QueryClient } from "@tanstack/react-query";
import { getCurrentUser, getTenantOverride } from "@/lib/auth-api";
import type { User } from "@/lib/auth-types";

/** Tyle z użytkownika wystarcza, żeby go jednoznacznie wskazać. */
type CacheIdentity = Pick<User, "id" | "tenantId">;

const STORAGE_KEY = "farmaster_rq_cache";

/**
 * Wersja zapisanego cache'u. Każdy deploy unieważnia całość, bo `BUSTER` niesie
 * sha commita wstrzykiwane przez `define` w `vite.config.ts` (to samo, którego
 * używa Sentry jako `release`).
 *
 * Dlaczego nie stała: zmiana kształtu odpowiedzi backendu przy niezmienionym
 * `queryKey` zhydratowałaby stare dane w nowy kod i widget renderowałby pola,
 * których już nie ma. Ręczne pamiętanie o bumpie stałej to dokładnie ten rodzaj
 * kroku, który się pomija.
 *
 * `dev` w fallbacku: lokalny build i testy nie mają sha, więc dzielą jeden
 * kubełek — tam i tak nie ma czego chronić.
 */
const BUSTER = `v1:${import.meta.env.VITE_SENTRY_RELEASE || "dev"}`;

/**
 * Po tylu milisekundach wpis jest wyrzucany niezależnie od `staleTime`.
 * 24 h to strop bezpieczeństwa, nie polityka świeżości — o refetch i tak
 * decyduje `staleTime` policzony z harmonogramu MV.
 */
const MAX_AGE_MS = 24 * 60 * 60_000;

/** Zapis rzadziej niż co sekundę — inaczej każdy wynik przepisuje cały blob. */
const WRITE_THROTTLE_MS = 1_000;

/**
 * `localStorage` na origin to ~5 MB dzielone z layoutami DataTable i scope'ami.
 * Powyżej tego progu odpuszczamy zapis zamiast wywalać cudze klucze.
 */
const MAX_BYTES = 2_000_000;

/** Rodziny kluczy, które wolno utrwalić. Prefiks porównywany po kolei. */
const PERSISTED_KEY_PREFIXES: readonly (readonly unknown[])[] = [["raporty", "dashboard"]];

interface PersistedBlob {
	buster: string;
	/** Wynik [`ownerKey`] w chwili zapisu. */
	owner: string;
	savedAt: number;
	state: ReturnType<typeof dehydrate>;
}

/**
 * Tożsamość, do której należy cache: użytkownik (organizacja macierzysta + id)
 * ORAZ aktualnie przepięta organizacja.
 *
 * Organizacja macierzysta, bo `users.user_id` jest IDENTITY per schemat — samo
 * id nie odróżnia osoby z `prima` od osoby z `retiro`.
 *
 * Przepięty tenant MUSI być w kluczu, bo `queryKey` dashboardu go nie niesie,
 * a `X-Tenant-Override` żyje wyłącznie w pamięci — po F5 wraca organizacja
 * macierzysta. Bez tego super_admin przepięty na `retiro` zapisałby jego liczby,
 * a po odświeżeniu zobaczyłby je pod szyldem `prima`.
 */
function ownerKey(user: CacheIdentity | null = getCurrentUser()): string {
	const who = user ? `${user.tenantId}:${user.id}` : "anon";
	return `${who}:${getTenantOverride() ?? "home"}`;
}

function isPersistedKey(queryKey: readonly unknown[]): boolean {
	return PERSISTED_KEY_PREFIXES.some(
		(prefix) => prefix.length <= queryKey.length && prefix.every((p, i) => queryKey[i] === p),
	);
}

function readBlob(): PersistedBlob | null {
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		const blob = JSON.parse(raw) as PersistedBlob;
		if (blob.buster !== BUSTER) return null;
		if (!Number.isFinite(blob.savedAt) || Date.now() - blob.savedAt > MAX_AGE_MS) return null;
		return blob;
	} catch {
		// Uszkodzony JSON, wyłączony localStorage (Safari private) — brak cache'u
		// jest zawsze poprawnym stanem, więc nie ma tu czego zgłaszać.
		return null;
	}
}

/** Właściciel zapisanego cache'u; `null` gdy nic nie zapisano. */
export function persistedCacheOwner(): string | null {
	return readBlob()?.owner ?? null;
}

/** Kasuje zapisany cache. Wołane przy każdym przejściu stanu auth. */
export function dropPersistedQueryCache(): void {
	try {
		window.localStorage.removeItem(STORAGE_KEY);
	} catch {
		// Brak localStorage = nie ma czego kasować.
	}
}

/**
 * Kasuje cache, jeśli zapisała go INNA osoba niż ta, którą właśnie zwróciło
 * `/auth/me`. Druga warstwa obok [`dropPersistedQueryCache`] — łapie przypadek,
 * w którym poprzednia sesja skończyła się zamknięciem karty, bez wylogowania.
 *
 * Zwraca `true`, gdy cache został wyrzucony.
 */
export function assertPersistedCacheOwner(
	queryClient: QueryClient,
	user: CacheIdentity | null,
): boolean {
	const owner = persistedCacheOwner();
	if (owner === null || owner === ownerKey(user)) return false;
	dropPersistedQueryCache();
	queryClient.removeQueries({ predicate: (q) => isPersistedKey(q.queryKey) });
	return true;
}

/**
 * Wczytuje zapisany cache do klienta. Wołane RAZ, synchronicznie, przed
 * pierwszym renderem.
 */
export function hydratePersistedQueryCache(queryClient: QueryClient): void {
	const blob = readBlob();
	if (!blob) return;
	try {
		hydrate(queryClient, blob.state);
	} catch {
		dropPersistedQueryCache();
	}
}

/**
 * Podpina zapis cache'u pod zmiany w `QueryCache`. Zwraca funkcję
 * odsubskrybowującą (używa jej test; w aplikacji subskrypcja żyje do końca).
 */
export function subscribePersistedQueryCache(queryClient: QueryClient): () => void {
	let timer: ReturnType<typeof setTimeout> | null = null;

	const write = () => {
		timer = null;
		try {
			const state = dehydrate(queryClient, {
				// Zapisujemy wyłącznie rozstrzygnięte sukcesy z allowlisty. Wpis
				// `pending`/`error` po hydratacji udawałby stan, którego nie ma.
				shouldDehydrateQuery: (query) =>
					query.state.status === "success" &&
					isPersistedKey(query.queryKey) &&
					// Opt-out per zapytanie: duże listy zostają w pamięci karty.
					(query.meta as { persist?: boolean } | undefined)?.persist !== false,
				shouldDehydrateMutation: () => false,
			});
			if (state.queries.length === 0) {
				dropPersistedQueryCache();
				return;
			}
			const blob: PersistedBlob = {
				buster: BUSTER,
				owner: ownerKey(),
				savedAt: Date.now(),
				state,
			};
			const raw = JSON.stringify(blob);
			if (raw.length > MAX_BYTES) {
				// `return` zostawiłby na dysku STARĄ migawkę, którą kolejne F5
				// zhydratowałoby jako aktualną — cache zamarzłby w chwili, gdy
				// dashboard urósł ponad próg. Lepiej stracić przyspieszenie F5
				// niż malować ekran danymi sprzed godzin.
				dropPersistedQueryCache();
				return;
			}
			window.localStorage.setItem(STORAGE_KEY, raw);
		} catch {
			// QuotaExceeded albo brak localStorage — kasujemy wpis, żeby nie
			// zostawić po sobie połowicznego blobu, i działamy dalej bez cache'u.
			dropPersistedQueryCache();
		}
	};

	const unsubscribe = queryClient.getQueryCache().subscribe(() => {
		if (timer !== null) return;
		timer = setTimeout(write, WRITE_THROTTLE_MS);
	});

	return () => {
		if (timer !== null) clearTimeout(timer);
		unsubscribe();
	};
}
